import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  LogOut, 
  Settings, 
  ExternalLink, 
  Eye,
  Link2,
  Image,
  User
} from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a4b1710a-e8aa-40f2-90ed-6aa15c96d321/artifacts/w14lowsf_Disen%CC%83o%20sin%20ti%CC%81tulo.png";

// Use relative URL for API calls so it works on any domain
const getApiUrl = () => {
  if (window.location.hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  return '';
};

const ModeloDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for session token in URL (from Google OAuth callback)
    const params = new URLSearchParams(window.location.search);
    const sessionToken = params.get('session');
    if (sessionToken) {
      localStorage.setItem("jglinks_session", sessionToken);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Skip if user passed from previous page
    if (location.state?.user && !user) {
      setUser(location.state.user);
    }
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if authenticated
      const response = await fetch(`${getApiUrl()}/api/auth/me`, {
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}`
        }
      });

      if (!response.ok) {
        throw new Error("Not authenticated");
      }

      const userData = await response.json();
      setUser(userData);

      // If user doesn't have username, redirect to complete profile
      if (!userData.username) {
        navigate("/modelo/completar-perfil", { state: { user: userData } });
        return;
      }

      // Fetch profile
      const profileResponse = await fetch(`${getApiUrl()}/api/model/profile`, {
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}`
        }
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setProfile(profileData);
      }

    } catch (error) {
      console.error("Auth error:", error);
      navigate("/modelo/login");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${getApiUrl()}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}`
        }
      });
    } catch (e) {
      // Ignore errors
    }
    
    localStorage.removeItem("jglinks_session");
    toast.success("Sesión cerrada");
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="JG Models" className="h-10" />
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="text-right mr-2">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-white/40">@{user?.username}</p>
            </div>
            
            <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/40">
                  <User className="w-5 h-5" />
                </div>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Mi Panel</h1>
          <p className="text-white/60">Gestiona tu perfil y enlaces</p>
        </div>

        {/* Profile Card */}
        {profile ? (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Stats Cards */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Link2 className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{profile.links?.length || 0}</p>
                  <p className="text-sm text-white/50">Enlaces</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center">
                  <Image className="w-6 h-6 text-pink-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{profile.galleryImages?.length || 0}</p>
                  <p className="text-sm text-white/50">Fotos en galería</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{profile.active ? "Activo" : "Inactivo"}</p>
                  <p className="text-sm text-white/50">Estado</p>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          {profile ? (
            <>
              {/* Edit Profile Card */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-white/10">
                    {profile.avatar ? (
                      <img src={profile.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-8 h-8 text-white/40" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{profile.name}</h3>
                    <p className="text-white/50">@{profile.username}</p>
                  </div>
                </div>
                
                <p className="text-white/60 text-sm mb-4 line-clamp-2">
                  {profile.bio || "Sin biografía"}
                </p>

                <div className="flex gap-3">
                  <Link to="/modelo/editar-perfil" className="flex-1">
                    <Button className="w-full bg-white text-black hover:bg-white/90">
                      <Settings className="w-4 h-4 mr-2" />
                      Editar Perfil
                    </Button>
                  </Link>
                  <Link to={`/${profile.username}`} target="_blank">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4">Acciones rápidas</h3>
                <div className="space-y-3">
                  <Link 
                    to="/modelo/editar-perfil" 
                    state={{ section: "links" }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <Link2 className="w-5 h-5 text-blue-400" />
                    <span className="text-white">Gestionar enlaces</span>
                  </Link>
                  <Link 
                    to="/modelo/editar-perfil"
                    state={{ section: "gallery" }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <Image className="w-5 h-5 text-pink-400" />
                    <span className="text-white">Subir fotos a galería</span>
                  </Link>
                  <Link 
                    to="/modelo/editar-perfil"
                    state={{ section: "socials" }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <User className="w-5 h-5 text-purple-400" />
                    <span className="text-white">Redes sociales</span>
                  </Link>
                </div>
              </div>
            </>
          ) : (
            /* No Profile - Create One */
            <div className="md:col-span-2 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-2xl p-8 border border-white/10 text-center">
              <div className="w-20 h-20 rounded-full bg-white/10 mx-auto mb-6 flex items-center justify-center">
                <User className="w-10 h-10 text-white/40" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">¡Crea tu perfil!</h2>
              <p className="text-white/60 mb-6 max-w-md mx-auto">
                Todavía no tienes un perfil público. Crea uno ahora para empezar a compartir tus enlaces.
              </p>
              <Link to="/modelo/crear-perfil">
                <Button className="bg-white text-black hover:bg-white/90 px-8 h-12">
                  Crear mi perfil
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ModeloDashboard;
